import {
  normalizeRestrictedSqlMeasures,
  RESTRICTED_SQL_MEASURE_FORMATS,
} from "./customReportMeasures";

export const TIME_SERIES_TIMEZONE = "Asia/Kolkata";
export const isTimeSeriesReportLevel = (reportLevel) => String(reportLevel || "") === "customers";

export const PERIOD_COMPARISON_TABLE_VARIANT = "period_comparison_table";
export const PERIOD_COMPARISON_AXIS_FIELD = "crm_lead_created_at";
export const CUSTOMER_PERIOD_COMPARISON_AXIS_FIELDS = [
  { value: "crm_lead_created_at", label: "CRM Lead Created At" },
  { value: "first_interaction_time", label: "First Interaction Time" },
  { value: "last_interaction_time", label: "Last Interaction Time" },
];
export const CONVERSATION_PERIOD_COMPARISON_AXIS_FIELDS = [
  { value: "call_time", label: "Conversation Time" },
];
export const PERIOD_COMPARISON_AXIS_FIELDS = [
  ...CUSTOMER_PERIOD_COMPARISON_AXIS_FIELDS,
  ...CONVERSATION_PERIOD_COMPARISON_AXIS_FIELDS,
];
export const getPeriodComparisonAxisFields = (reportLevel) =>
  String(reportLevel || "customers") === "calls"
    ? CONVERSATION_PERIOD_COMPARISON_AXIS_FIELDS
    : CUSTOMER_PERIOD_COMPARISON_AXIS_FIELDS;
export const PERIOD_COMPARISON_TIMEZONE = "Asia/Kolkata";
export const PERIOD_COMPARISON_EMPLOYEE_FIELD = "metadata.employee_id";
export const PERIOD_COMPARISON_PERIODS = ["today", "yesterday", "last_7_days", "last_30_days"];
export const GENERIC_PERIOD_COMPARISON_MODE = "generic";
export const PERIOD_COMPARISON_MEASURE_PLACEMENTS = [
  { value: "columns", label: "Columns" },
  { value: "rows", label: "Rows" },
];
export const DEFAULT_PERIOD_COMPARISON_MEASURE_PLACEMENT = "rows";
export const MAX_PERIOD_COMPARISON_DIMENSIONS = 10;
export const PERIOD_COMPARISON_TYPES = [
  { value: "team_areas", label: "Team overview" },
  { value: "employee_positive_overall", label: "Employee positive overview" },
  { value: "employee_positive_areas", label: "Employee positive areas" },
  { value: "employee_negative_overall", label: "Employee negative overview" },
  { value: "employee_negative_areas", label: "Employee negative areas" },
];
export const MAX_PERIOD_COMPARISON_AREAS = 10;

const GENERIC_PERIOD_COMPARISON_MEASURE_FORMATS = new Set(
  RESTRICTED_SQL_MEASURE_FORMATS.map((format) => format.value)
);

const normalizePeriodComparisonArea = (area = {}) => ({
  id: String(area?.id || ""),
  label: String(area?.label || ""),
  field: String(area?.field || ""),
  polarity: String(area?.polarity || ""),
});

const normalizePeriodComparisonPair = (pair) => {
  if (!pair || typeof pair !== "object" || Array.isArray(pair)) return null;
  const module = String(pair.module || "").trim();
  const value = String(pair.value || "").trim();
  return module && value ? { module, value } : null;
};

export const isLegacyPeriodComparisonConfig = (periodComparison) => {
  if (!periodComparison || typeof periodComparison !== "object" || Array.isArray(periodComparison)) {
    return false;
  }
  if (periodComparison.mode === GENERIC_PERIOD_COMPARISON_MODE) return false;
  const comparisonType = periodComparison.comparisonType ?? periodComparison.comparison_type;
  return Boolean(
    PERIOD_COMPARISON_TYPES.some((option) => option.value === comparisonType) ||
      Array.isArray(periodComparison.areas) ||
      periodComparison.employeeField ||
      periodComparison.employee_field
  );
};

const normalizePeriodComparisonPeriods = (periods) =>
  Array.isArray(periods)
    ? periods.map((period) => String(period))
    : [...PERIOD_COMPARISON_PERIODS];

export const normalizePeriodComparisonConfig = (periodComparison, reportLevel = "customers") => {
  const raw =
    periodComparison && typeof periodComparison === "object" && !Array.isArray(periodComparison)
      ? periodComparison
      : {};
  if (!isLegacyPeriodComparisonConfig(raw)) {
    const allowedAxisFields = getPeriodComparisonAxisFields(reportLevel);
    const axisField = allowedAxisFields.some(
      (option) => option.value === raw.axisField
    )
      ? raw.axisField
      : allowedAxisFields[0].value;
    return {
      mode: GENERIC_PERIOD_COMPARISON_MODE,
      axisField,
      timezone: PERIOD_COMPARISON_TIMEZONE,
      periods: normalizePeriodComparisonPeriods(raw.periods),
      measurePlacement: PERIOD_COMPARISON_MEASURE_PLACEMENTS.some(
        (option) => option.value === (raw.measurePlacement ?? raw.measure_placement)
      )
        ? raw.measurePlacement ?? raw.measure_placement
        : DEFAULT_PERIOD_COMPARISON_MEASURE_PLACEMENT,
      columns: (Array.isArray(raw.columns) ? raw.columns : [])
        .map(normalizePeriodComparisonPair)
        .filter(Boolean),
    };
  }
  return {
    comparisonType: String(raw.comparisonType ?? raw.comparison_type ?? ""),
    axisField: PERIOD_COMPARISON_AXIS_FIELD,
    timezone: PERIOD_COMPARISON_TIMEZONE,
    periods: normalizePeriodComparisonPeriods(raw.periods),
    employeeField: PERIOD_COMPARISON_EMPLOYEE_FIELD,
    areas: (Array.isArray(raw.areas) ? raw.areas : []).map(normalizePeriodComparisonArea),
  };
};

export const getDefaultPeriodComparisonConfig = (reportLevel = "customers") =>
  normalizePeriodComparisonConfig({
    mode: GENERIC_PERIOD_COMPARISON_MODE,
    measurePlacement: "rows",
  }, reportLevel);

const getConfiguredParameterPairs = (visualization) => {
  const orderedPairs = visualization?.orderedParameterPairs ?? visualization?.ordered_parameter_pairs;
  if (Array.isArray(orderedPairs)) {
    return orderedPairs.map(normalizePeriodComparisonPair).filter(Boolean);
  }
  const parameters = visualization?.parameters;
  if (!parameters || typeof parameters !== "object" || Array.isArray(parameters)) return [];
  return Object.entries(parameters).flatMap(([module, values]) =>
    (Array.isArray(values) ? values : [])
      .map((value) => normalizePeriodComparisonPair({ module, value }))
      .filter(Boolean)
  );
};

const getConfiguredParameterCount = (visualization) =>
  getConfiguredParameterPairs(visualization).length;

export const validatePeriodComparisonVisualization = (visualization, reportLevel) => {
  if (
    visualization?.visualizationType !== "table" ||
    visualization?.variant !== PERIOD_COMPARISON_TABLE_VARIANT
  ) {
    return [];
  }

  const errors = [];
  const raw = visualization?.periodComparison ?? visualization?.period_comparison;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return [
      {
        field: "periodComparison",
        message: "Configure the period comparison before saving this visualization.",
      },
    ];
  }
  const config = normalizePeriodComparisonConfig(raw, reportLevel);
  const isLegacy = isLegacyPeriodComparisonConfig(raw);
  if (isLegacy && !isTimeSeriesReportLevel(reportLevel)) {
    errors.push({
      field: "periodComparison",
      message: "Legacy Period Comparison Table is available only for customer-level reports.",
    });
  }
  if (
    isLegacy &&
    !PERIOD_COMPARISON_TYPES.some((option) => option.value === config.comparisonType)
  ) {
    errors.push({
      field: "periodComparison.comparisonType",
      message: "Select a supported period comparison type.",
    });
  }
  const requestedAxisField = String(raw.axisField ?? raw.axis_field ?? "");
  if (
    (isLegacy && requestedAxisField !== PERIOD_COMPARISON_AXIS_FIELD) ||
    (!isLegacy &&
      !getPeriodComparisonAxisFields(reportLevel).some(
        (option) => option.value === requestedAxisField
      ))
  ) {
    errors.push({
      field: "periodComparison.axisField",
      message: isLegacy
        ? "Legacy period comparisons must use CRM Lead Created At."
        : String(reportLevel) === "calls"
          ? "Conversation-level period comparisons must use Conversation Time."
          : "Select CRM Lead Created At, First Interaction Time, or Last Interaction Time.",
    });
  }
  if (config.timezone !== PERIOD_COMPARISON_TIMEZONE) {
    errors.push({
      field: "periodComparison.timezone",
      message: `Period comparison timezone must be ${PERIOD_COMPARISON_TIMEZONE}.`,
    });
  }
  const orderedSelectedPeriods = PERIOD_COMPARISON_PERIODS.filter((period) =>
    config.periods.includes(period)
  );
  if (
    config.periods.length === 0 ||
    config.periods.length > PERIOD_COMPARISON_PERIODS.length ||
    orderedSelectedPeriods.length !== config.periods.length ||
    !orderedSelectedPeriods.every((period, index) => period === config.periods[index])
  ) {
    errors.push({
      field: "periodComparison.periods",
      message: "Select at least one comparison period without duplicates.",
    });
  }
  if (isLegacy && config.employeeField !== PERIOD_COMPARISON_EMPLOYEE_FIELD) {
    errors.push({
      field: "periodComparison.employeeField",
      message: "Period comparisons must group employees by Metadata Employee ID.",
    });
  }
  const parameterCount = getConfiguredParameterCount(visualization);
  if (isLegacy && parameterCount > 0) {
    errors.push({
      field: "parameters",
      message: "Period Comparison Table does not support additional grouping parameters.",
    });
  }
  if (isLegacy && Array.isArray(visualization?.measures) && visualization.measures.length > 0) {
    errors.push({
      field: "measures",
      message: "Period Comparison Table uses its fixed period metrics and does not support SQL measures.",
    });
  }
  if (!isLegacy) {
    if (config.mode !== GENERIC_PERIOD_COMPARISON_MODE) {
      errors.push({
        field: "periodComparison.mode",
        message: "Period comparison mode must be generic.",
      });
    }
    const rawMeasurePlacement = raw.measurePlacement ?? raw.measure_placement;
    if (
      rawMeasurePlacement !== undefined &&
      !PERIOD_COMPARISON_MEASURE_PLACEMENTS.some(
        (option) => option.value === rawMeasurePlacement
      )
    ) {
      errors.push({
        field: "periodComparison.measurePlacement",
        message: "Display measures as Columns or Rows.",
      });
    }
    if (parameterCount > MAX_PERIOD_COMPARISON_DIMENSIONS) {
      errors.push({
        field: "parameters",
        message: `Period Comparison Table supports at most ${MAX_PERIOD_COMPARISON_DIMENSIONS} grouping parameters.`,
      });
    }
    const selectedPairs = getConfiguredParameterPairs(visualization);
    const selectedIdentities = new Set(
      selectedPairs.map((pair) => `${pair.module}\u0000${pair.value}`)
    );
    const columnIdentities = config.columns.map(
      (pair) => `${pair.module}\u0000${pair.value}`
    );
    if (config.columns.length > MAX_PERIOD_COMPARISON_DIMENSIONS) {
      errors.push({
        field: "periodComparison.columns",
        message: `Period Comparison Table supports at most ${MAX_PERIOD_COMPARISON_DIMENSIONS} column parameters.`,
      });
    }
    if (
      new Set(columnIdentities).size !== columnIdentities.length ||
      columnIdentities.some((identity) => !selectedIdentities.has(identity))
    ) {
      errors.push({
        field: "periodComparison.columns",
        message: "Column parameters must be a unique subset of the selected grouping parameters.",
      });
    }
    const hasMeasures = Array.isArray(visualization?.measures) && visualization.measures.length > 0;
    if (hasMeasures) {
      normalizeRestrictedSqlMeasures(visualization.measures).forEach((measure, index) => {
        if (!GENERIC_PERIOD_COMPARISON_MEASURE_FORMATS.has(measure.format)) {
          errors.push({
            measureId: measure.id,
            measureIndex: index,
            field: "format",
            message:
              "Period comparison measures support Integer, Decimal, or Percentage formats.",
          });
        }
      });
    }
    const expectedCountField = String(reportLevel) === "calls" ? "id" : "customer_id";
    if (
      !hasMeasures &&
      (visualization?.aggregationParameter !== expectedCountField ||
        visualization?.aggregationOperator !== "count")
    ) {
      errors.push({
        field: "aggregationOperator",
        message: `Standard period comparison values must count ${
          String(reportLevel) === "calls" ? "Conversation ID" : "Customer ID"
        }.`,
      });
    }
    return errors;
  }

  if (config.areas.length === 0) {
    errors.push({
      field: "periodComparison.areas",
      message: "Add at least one configured area.",
    });
  } else if (config.areas.length > MAX_PERIOD_COMPARISON_AREAS) {
    errors.push({
      field: "periodComparison.areas",
      message: `Period comparisons support at most ${MAX_PERIOD_COMPARISON_AREAS} areas.`,
    });
  }

  const seenIds = new Set();
  config.areas.forEach((area, index) => {
    const fieldPrefix = `periodComparison.areas.${index}`;
    const trimmedLabel = area.label.trim();
    if (!/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(area.id)) {
      errors.push({
        field: `${fieldPrefix}.id`,
        message: `Area ${index + 1} has an invalid ID.`,
      });
    } else if (seenIds.has(area.id)) {
      errors.push({
        field: `${fieldPrefix}.id`,
        message: "Area IDs must be unique.",
      });
    }
    seenIds.add(area.id);
    if (!trimmedLabel) {
      errors.push({
        field: `${fieldPrefix}.label`,
        message: `Enter a label for area ${index + 1}.`,
      });
    } else if (trimmedLabel.length > 100) {
      errors.push({
        field: `${fieldPrefix}.label`,
        message: "Area labels must be 100 characters or fewer.",
      });
    } else if (
      /^[=+\-@]/.test(trimmedLabel) ||
      Array.from(trimmedLabel).some((character) => character.charCodeAt(0) < 32)
    ) {
      errors.push({
        field: `${fieldPrefix}.label`,
        message: "Area labels cannot begin with =, +, -, or @ or contain control characters.",
      });
    }
    if (!/^custom_module\.[a-z0-9_]+$/.test(area.field)) {
      errors.push({
        field: `${fieldPrefix}.field`,
        message: `Select a Custom Module field for area ${index + 1}.`,
      });
    }
    if (!["positive", "negative"].includes(area.polarity)) {
      errors.push({
        field: `${fieldPrefix}.polarity`,
        message: `Select a polarity for area ${index + 1}.`,
      });
    }
  });

  if (config.comparisonType === "team_areas") {
    if (!config.areas.some((area) => area.polarity === "positive")) {
      errors.push({
        field: "periodComparison.areas",
        message: "Team overview requires at least one positive area.",
      });
    }
    if (!config.areas.some((area) => area.polarity === "negative")) {
      errors.push({
        field: "periodComparison.areas",
        message: "Team overview requires at least one negative area.",
      });
    }
  }
  if (
    config.comparisonType.startsWith("employee_positive_") &&
    config.areas.some((area) => area.polarity !== "positive")
  ) {
    errors.push({
      field: "periodComparison.areas",
      message: "Employee positive comparisons can contain only positive areas.",
    });
  }
  if (
    config.comparisonType.startsWith("employee_negative_") &&
    config.areas.some((area) => area.polarity !== "negative")
  ) {
    errors.push({
      field: "periodComparison.areas",
      message: "Employee negative comparisons can contain only negative areas.",
    });
  }
  return errors;
};

export const TIME_SERIES_FIELDS = [
  {
    value: "crm_lead_created_at",
    label: "CRM Lead Created At",
    filterKey: "crm_lead_created_at",
    startKey: "crm_lead_created_at_start",
    endKey: "crm_lead_created_at_end",
    rangeTypeKey: "crm_lead_created_at_range_type",
  },
  {
    value: "first_interaction_time",
    label: "First Interaction",
    filterKey: "firstinteraction",
    startKey: "firstinteraction_start",
    endKey: "firstinteraction_end",
    rangeTypeKey: "firstinteraction_range_type",
  },
  {
    value: "last_interaction_time",
    label: "Last Interaction",
    filterKey: "lastinteraction",
    startKey: "lastinteraction_start",
    endKey: "lastinteraction_end",
    rangeTypeKey: "lastinteraction_range_type",
  },
];

export const TIME_SERIES_GRAINS = [
  { value: "hour", label: "Hour", maxDays: 7, limitLabel: "up to 7 days" },
  { value: "day", label: "Day", maxDays: 366, limitLabel: "up to 366 days" },
  { value: "week", label: "Week", maxDays: 1095, limitLabel: "up to 1,095 days" },
  { value: "month", label: "Month", maxDays: 3650, limitLabel: "up to 3,650 days" },
];

export const TIME_SERIES_DISPLAY_MODES = [
  { value: "trendline", label: "Trendline" },
  { value: "table", label: "Time-series Table" },
];

export const TIME_RANGE_PRESETS = [
  "Today",
  "Yesterday",
  "This Week",
  "This Month",
  "Last Month",
  "Last 7 Days",
  "Last 30 Days",
  "Last 3 Months",
  "Custom Range",
];

export const MAX_PIVOT_DIMENSIONS = 10;
export const MAX_PIVOT_ROWS = MAX_PIVOT_DIMENSIONS;
export const MAX_PIVOT_COLUMNS = MAX_PIVOT_DIMENSIONS;
export const MAX_RENDERED_PIVOT_CELLS = 10000;

const normalizePair = (pair) => {
  if (!pair || !pair.module || pair.value === null || pair.value === undefined) return null;
  const value = String(pair.value).trim();
  if (!value) return null;
  return { module: String(pair.module), value };
};

export const pairIdentity = (pair) => `${pair?.module || ""}\u0000${pair?.value || ""}`;

const normalizePairs = (pairs, max) =>
  (Array.isArray(pairs) ? pairs : []).map(normalizePair).filter(Boolean).slice(0, max);

export const getPeriodComparisonAxes = (periodComparison, selectedPairs = []) => {
  const selected = normalizePairs(selectedPairs, MAX_PERIOD_COMPARISON_DIMENSIONS);
  const selectedIdentities = new Set(selected.map(pairIdentity));
  const columns = normalizePairs(
    periodComparison?.columns,
    MAX_PERIOD_COMPARISON_DIMENSIONS
  ).filter((pair) => selectedIdentities.has(pairIdentity(pair)));
  const columnIdentities = new Set(columns.map(pairIdentity));
  return {
    rows: selected.filter((pair) => !columnIdentities.has(pairIdentity(pair))),
    columns,
  };
};

export const movePeriodComparisonPair = (
  axes,
  fromShelf,
  fromIndex,
  toShelf,
  toIndex
) => {
  const rows = [...(axes?.rows || [])];
  const columns = [...(axes?.columns || [])];
  const source = fromShelf === "columns" ? columns : rows;
  const target = toShelf === "columns" ? columns : rows;
  if (fromIndex < 0 || fromIndex >= source.length) return { rows, columns };
  if (
    fromShelf !== toShelf &&
    target.length >= MAX_PERIOD_COMPARISON_DIMENSIONS
  ) {
    return { rows, columns };
  }
  const [pair] = source.splice(fromIndex, 1);
  const boundedIndex = Math.max(0, Math.min(Number(toIndex) || 0, target.length));
  target.splice(boundedIndex, 0, pair);
  return { rows, columns };
};

export const normalizeTimeSeriesConfig = (timeSeries) => ({
  field: String(timeSeries?.field || "crm_lead_created_at"),
  grain: String(timeSeries?.grain || "day"),
  timezone: TIME_SERIES_TIMEZONE,
  displayMode: String(timeSeries?.displayMode || "trendline"),
});

export const getTimeSeriesField = (field) =>
  TIME_SERIES_FIELDS.find((option) => option.value === field) || null;

const parseIsoDate = (value) => {
  const raw = String(value || "");
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;
  const parsed = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(parsed.getTime())) return null;
  if (
    parsed.getUTCFullYear() !== Number(match[1]) ||
    parsed.getUTCMonth() !== Number(match[2]) - 1 ||
    parsed.getUTCDate() !== Number(match[3])
  ) {
    return null;
  }
  return parsed;
};

const diffDaysInclusive = (start, end) =>
  Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;

export const estimateTimeRangeDays = (filterValue, startValue, endValue, now = new Date()) => {
  const preset = String(filterValue || "");
  const fixedPresetDays = {
    Today: 1,
    Yesterday: 1,
    "This Week": 7,
    "Last 7 Days": 7,
    "Last 30 Days": 30,
    "Last 3 Months": 90,
  };
  if (fixedPresetDays[preset]) return fixedPresetDays[preset];
  if (preset === "This Month" || preset === "Last Month") return 31;
  if (preset !== "Custom Range") return null;
  const start = parseIsoDate(startValue);
  const end = parseIsoDate(endValue);
  if (!start) return null;
  const effectiveEnd = end || new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  if (effectiveEnd < start) return -1;
  return diffDaysInclusive(start, effectiveEnd);
};

export const ensureTimeSeriesRangeFilter = (
  advancedFilters,
  field,
  { clearImplicitLastInteraction = false, previousField = null } = {}
) => {
  const descriptor = getTimeSeriesField(field);
  const next = { ...(advancedFilters || {}) };
  const previousDescriptor = getTimeSeriesField(previousField);
  if (
    previousDescriptor &&
    previousDescriptor.value !== field &&
    next[previousDescriptor.filterKey] === "Last 7 Days" &&
    !next[previousDescriptor.startKey] &&
    !next[previousDescriptor.endKey] &&
    !next[previousDescriptor.rangeTypeKey]
  ) {
    next[previousDescriptor.filterKey] = "";
  }
  if (
    clearImplicitLastInteraction &&
    field !== "last_interaction_time" &&
    next.lastinteraction === "Last 7 Days" &&
    !next.lastinteraction_start &&
    !next.lastinteraction_end &&
    !next.lastinteraction_range_type
  ) {
    next.lastinteraction = "";
  }
  if (descriptor && !next[descriptor.filterKey]) next[descriptor.filterKey] = "Last 7 Days";
  return next;
};

export const validateTimeSeriesVisualization = (visualization, now = new Date()) => {
  if (visualization?.visualizationType !== "time_series") return [];
  const config = visualization?.timeSeries;
  const errors = [];
  const orderedPairs = visualization?.orderedParameterPairs ?? visualization?.ordered_parameter_pairs;
  const parameterCount =
    visualization?.parameters && typeof visualization.parameters === "object" && !Array.isArray(visualization.parameters)
      ? Object.values(visualization.parameters).reduce(
          (sum, values) => sum + (Array.isArray(values) ? values.length : 0),
          0
        )
      : 0;
  if ((Array.isArray(orderedPairs) && orderedPairs.length > 0) || parameterCount > 0) {
    errors.push({ field: "parameters", message: "Time Series does not support additional grouping parameters." });
  }
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return [{ field: "timeSeries", message: "Configure the time field and grain." }];
  }
  const field = getTimeSeriesField(config.field);
  if (!field) errors.push({ field: "timeSeries.field", message: "Select a supported customer time field." });
  const grain = TIME_SERIES_GRAINS.find((option) => option.value === config.grain);
  if (!grain) errors.push({ field: "timeSeries.grain", message: "Select hour, day, week, or month." });
  if (config.timezone !== TIME_SERIES_TIMEZONE) {
    errors.push({ field: "timeSeries.timezone", message: `Timezone must be ${TIME_SERIES_TIMEZONE}.` });
  }
  if (!TIME_SERIES_DISPLAY_MODES.some((option) => option.value === config.displayMode)) {
    errors.push({
      field: "timeSeries.displayMode",
      message: "Select Trendline or Time-series Table.",
    });
  }
  if (!field) return errors;
  const filters = visualization?.advancedFilters || {};
  const filterValue = filters[field.filterKey];
  if (!filterValue) {
    errors.push({ field: "timeSeries.range", message: `Choose a ${field.label} time range.` });
    return errors;
  }
  if (filterValue === "Custom Range") {
    if (!filters[field.startKey] || !filters[field.endKey]) {
      errors.push({ field: "timeSeries.range", message: "Choose both custom range dates." });
      return errors;
    }
    if (!parseIsoDate(filters[field.startKey]) || !parseIsoDate(filters[field.endKey])) {
      errors.push({ field: "timeSeries.range", message: "Choose valid custom range dates." });
      return errors;
    }
  }
  const rangeDays = estimateTimeRangeDays(
    filterValue,
    filters[field.startKey],
    filters[field.endKey],
    now
  );
  if (rangeDays === -1) {
    errors.push({ field: "timeSeries.range", message: "The time range end must not precede its start." });
  } else if (grain && rangeDays !== null && rangeDays > grain.maxDays) {
    errors.push({
      field: "timeSeries.range",
      message: `${grain.label} grain supports ${grain.limitLabel}; narrow the selected range.`,
    });
  }
  return errors;
};

export const getLegacyPivotConfig = (orderedPairs) => {
  const pairs = normalizePairs(orderedPairs, 2);
  if (pairs.length !== 2) return null;
  return { columns: [pairs[0]], rows: [pairs[1]], isLegacy: true };
};

export const getPivotConfig = (visualization, orderedPairs = []) => {
  const raw = visualization?.pivotConfig;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return {
      rows: normalizePairs(raw.rows, MAX_PIVOT_ROWS),
      columns: normalizePairs(raw.columns, MAX_PIVOT_COLUMNS),
      isLegacy: false,
    };
  }
  return getLegacyPivotConfig(orderedPairs) || { rows: [], columns: [], isLegacy: false };
};

export const buildDefaultPivotConfig = (orderedPairs) => {
  const pairs = normalizePairs(orderedPairs, MAX_PIVOT_DIMENSIONS);
  if (pairs.length < 2) return { rows: [], columns: [] };
  const columnCount = pairs.length >= Math.ceil(MAX_PIVOT_DIMENSIONS / 2) ? 2 : 1;
  return {
    columns: pairs.slice(0, columnCount),
    rows: pairs.slice(columnCount, columnCount + MAX_PIVOT_ROWS),
  };
};

export const reconcilePivotConfig = (pivotConfig, selectedPairs) => {
  const selected = normalizePairs(selectedPairs, MAX_PIVOT_DIMENSIONS);
  const selectedIds = new Set(selected.map(pairIdentity));
  const existingRows = normalizePairs(pivotConfig?.rows, MAX_PIVOT_ROWS).filter((pair) =>
    selectedIds.has(pairIdentity(pair))
  );
  const existingColumns = normalizePairs(pivotConfig?.columns, MAX_PIVOT_COLUMNS).filter((pair) =>
    selectedIds.has(pairIdentity(pair))
  );
  const assigned = new Set([...existingRows, ...existingColumns].map(pairIdentity));
  const unassigned = selected.filter((pair) => !assigned.has(pairIdentity(pair)));
  const rows = [...existingRows];
  const columns = [...existingColumns];
  unassigned.forEach((pair) => {
    if (rows.length < MAX_PIVOT_ROWS) rows.push(pair);
    else if (columns.length < MAX_PIVOT_COLUMNS) columns.push(pair);
  });
  if (columns.length === 0 && rows.length > 1) columns.push(rows.shift());
  if (rows.length === 0 && columns.length > 1) rows.push(columns.pop());
  return { rows, columns };
};

export const movePivotPair = (pivotConfig, fromShelf, fromIndex, toShelf, toIndex) => {
  const rows = [...(pivotConfig?.rows || [])];
  const columns = [...(pivotConfig?.columns || [])];
  const source = fromShelf === "columns" ? columns : rows;
  const target = toShelf === "columns" ? columns : rows;
  if (fromIndex < 0 || fromIndex >= source.length) return { rows, columns };
  if (toShelf === "rows" && fromShelf !== "rows" && rows.length >= MAX_PIVOT_ROWS) return { rows, columns };
  if (toShelf === "columns" && fromShelf !== "columns" && columns.length >= MAX_PIVOT_COLUMNS) {
    return { rows, columns };
  }
  const [pair] = source.splice(fromIndex, 1);
  const boundedIndex = Math.max(0, Math.min(Number(toIndex) || 0, target.length));
  target.splice(boundedIndex, 0, pair);
  return { rows, columns };
};

export const validatePivotConfig = (visualization, orderedPairs = []) => {
  const isPivot = visualization?.visualizationType === "table" && visualization?.variant === "pivot_table";
  if (!isPivot) return [];
  const rawConfig = visualization?.pivotConfig;
  const rawRows =
    rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig)
      ? (Array.isArray(rawConfig.rows) ? rawConfig.rows : [])
      : getLegacyPivotConfig(orderedPairs)?.rows || [];
  const rawColumns =
    rawConfig && typeof rawConfig === "object" && !Array.isArray(rawConfig)
      ? (Array.isArray(rawConfig.columns) ? rawConfig.columns : [])
      : getLegacyPivotConfig(orderedPairs)?.columns || [];
  const config = {
    rows: rawRows.map(normalizePair).filter(Boolean),
    columns: rawColumns.map(normalizePair).filter(Boolean),
  };
  const errors = [];
  if (config.rows.length < 1) errors.push({ field: "pivotConfig.rows", message: "Add at least one row dimension." });
  if (config.columns.length < 1) {
    errors.push({ field: "pivotConfig.columns", message: "Add at least one column dimension." });
  }
  if (config.rows.length > MAX_PIVOT_ROWS) {
    errors.push({ field: "pivotConfig.rows", message: `Pivot rows support at most ${MAX_PIVOT_ROWS} dimensions.` });
  }
  if (config.columns.length > MAX_PIVOT_COLUMNS) {
    errors.push({
      field: "pivotConfig.columns",
      message: `Pivot columns support at most ${MAX_PIVOT_COLUMNS} dimensions.`,
    });
  }
  const allPairs = [...config.rows, ...config.columns];
  if (allPairs.length > MAX_PIVOT_DIMENSIONS) {
    errors.push({ field: "pivotConfig", message: `A pivot supports at most ${MAX_PIVOT_DIMENSIONS} dimensions.` });
  }
  const identities = allPairs.map(pairIdentity);
  if (new Set(identities).size !== identities.length) {
    errors.push({ field: "pivotConfig", message: "A dimension cannot appear on both pivot shelves." });
  }
  const selectedPairs = (Array.isArray(orderedPairs) ? orderedPairs : []).map(normalizePair).filter(Boolean);
  const selectedIdentities = selectedPairs.map(pairIdentity);
  if (new Set(selectedIdentities).size !== selectedIdentities.length) {
    errors.push({ field: "parameters", message: "Each grouping parameter can be selected only once." });
  }
  const shelfSet = new Set(identities);
  const selectedSet = new Set(selectedIdentities);
  if (
    shelfSet.size !== selectedSet.size ||
    [...shelfSet].some((identity) => !selectedSet.has(identity))
  ) {
    errors.push({
      field: "pivotConfig",
      message: "Pivot shelves must contain every selected grouping parameter exactly once.",
    });
  }
  return errors;
};

export const tupleKey = (values) => JSON.stringify(values.map((value) => value ?? null));

export const buildMultidimensionalPivotModel = ({
  rows,
  rowKeys,
  columnKeys,
  measureColumns,
}) => {
  const rowTuples = [];
  const columnTuples = [];
  const seenRows = new Set();
  const seenColumns = new Set();
  const cellByTuple = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const rowValues = rowKeys.map((key) => row?.[key]);
    const columnValues = columnKeys.map((key) => row?.[key]);
    const rowIdentity = tupleKey(rowValues);
    const columnIdentity = tupleKey(columnValues);
    if (!seenRows.has(rowIdentity)) {
      seenRows.add(rowIdentity);
      rowTuples.push({ key: rowIdentity, values: rowValues });
    }
    if (!seenColumns.has(columnIdentity)) {
      seenColumns.add(columnIdentity);
      columnTuples.push({ key: columnIdentity, values: columnValues });
    }
    cellByTuple.set(`${rowIdentity}\u0000${columnIdentity}`, row);
  });
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  const compareTuples = (left, right) => {
    const length = Math.max(left.values.length, right.values.length);
    for (let index = 0; index < length; index += 1) {
      const comparison = collator.compare(
        left.values[index] === null || left.values[index] === undefined ? "" : String(left.values[index]),
        right.values[index] === null || right.values[index] === undefined ? "" : String(right.values[index])
      );
      if (comparison !== 0) return comparison;
    }
    return 0;
  };
  rowTuples.sort(compareTuples);
  columnTuples.sort(compareTuples);
  return { rowTuples, columnTuples, measureColumns, cellByTuple };
};

export const getHierarchicalRowSpan = (rowTuples, rowIndex, level) => {
  const tuple = rowTuples[rowIndex];
  if (!tuple) return 0;
  const samePrefix = (left, right) => {
    for (let index = 0; index <= level; index += 1) {
      if ((left?.values?.[index] ?? null) !== (right?.values?.[index] ?? null)) return false;
    }
    return true;
  };
  if (rowIndex > 0 && samePrefix(tuple, rowTuples[rowIndex - 1])) return 0;
  let span = 1;
  for (let index = rowIndex + 1; index < rowTuples.length; index += 1) {
    if (!samePrefix(tuple, rowTuples[index])) break;
    span += 1;
  }
  return span;
};

export const buildColumnHeaderGroups = (columnTuples, level, measureCount) => {
  const groups = [];
  let index = 0;
  while (index < columnTuples.length) {
    const tuple = columnTuples[index];
    let count = 1;
    while (index + count < columnTuples.length) {
      const next = columnTuples[index + count];
      let samePrefix = true;
      for (let prefix = 0; prefix <= level; prefix += 1) {
        if ((tuple.values[prefix] ?? null) !== (next.values[prefix] ?? null)) {
          samePrefix = false;
          break;
        }
      }
      if (!samePrefix) break;
      count += 1;
    }
    groups.push({
      key: `${tuple.key}:${level}`,
      value: tuple.values[level],
      colSpan: count * measureCount,
    });
    index += count;
  }
  return groups;
};
